/**
 * Y2K wallpapers — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_wallpapers_iv`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const y2kWallpapersPack: ToilePack = {
    slug: 'y2k-wallpapers',
    category: 'screens',
    name: 'Y2K wallpapers',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 768, h: 1344},
    readings: [
        {key: 'chrome', label: 'Chrome hearts', subject: 'glossy liquid-chrome hearts and stars floating on a pink-to-lilac gradient, small sparkles around them'},
        {key: 'butterflies', label: 'Butterflies', subject: 'glittery pink and blue butterflies and four-point sparkles on a baby blue ground, with a few pearl beads'},
        {key: 'flipphone', label: 'Flip phone', subject: 'a pink glossy flip phone hung with bead charms and a tiny star, lying on a fluffy pink surface'},
    ],
    styles: [
        {key: 'gloss', label: 'Glossy 3D', style: 'glossy 3D render, liquid chrome and jelly plastic, specular highlights, soft airbrush glow', palette: 'baby pink, silver chrome and lilac', framing: 'Full-bleed, the objects floating in the lower two thirds'},
        {key: 'scrapbook', label: 'Scrapbook', style: 'digital scrapbook collage, glitter stickers, gel pen doodles, tiny rhinestones and torn paper', palette: 'hot pink, baby blue and silver glitter', framing: 'Full-bleed, stickers scattered across the lower two thirds'},
        {key: 'webcore', label: 'Web 2000', style: 'early 2000s web graphic look, pixel sparkles, soft gradient bars, bevelled shapes, low-res charm', palette: 'aqua, pink and white on a pale gradient', framing: 'Full-bleed, the motif centered low on a gradient ground'},
    ],
    directions: ['butterfly clips', 'cherry red', 'lip gloss'],
    directionHint: 'try: butterfly clips',
    seo: {
        title: 'Y2K Wallpapers: Chrome Hearts, Butterflies, Flip Phone',
        metaDescription: 'Y2K phone wallpapers in pink and silver: chrome hearts, sparkly butterflies and a flip phone with charms, each in three styles.',
        h1: 'Y2K wallpapers: chrome hearts, butterflies, a flip phone',
        tagline: 'Nine glossy phone wallpapers, three turn-of-the-millennium scenes in three styles.',
        intro: 'Phone wallpapers in the Y2K look: glossy chrome hearts and stars on pink, glittery butterflies with sparkles, and a pink flip phone hung with bead charms. Each comes as a glossy 3D render, a digital scrapbook collage and an early-2000s web graphic. Teens, and anyone who loves the turn of the millennium, can set them as they are. Type a different subject, such as lip gloss or cherry red, and the styles carry it.',
        useCases: [
            'a phone lock screen',
            'a retro-themed party or gift',
            'a teen bedroom print',
            'a tablet background',
        ],
        faq: [
            {q: 'What are the nine pictures?', a: 'Three scenes: chrome hearts and stars, butterflies with sparkles, and a flip phone with charms. Each scene is shown in three styles.'},
            {q: 'Can I change the subject?', a: 'Yes. Type a subject such as butterfly clips, cherry red or lip gloss; a typed subject is a request, not a promised result.'},
            {q: 'How do the three styles differ?', a: 'Glossy 3D uses chrome and jelly plastic with bright highlights. Scrapbook has glitter stickers, gel pen doodles and torn paper. Web 2000 has pixel sparkles, bevelled shapes and a low-resolution charm.'},
            {q: 'Are these photographs?', a: 'No. All nine pictures are rendered images in the styles named.'},
        ],
    },
}
